import React, { useEffect, useState } from "react";
import Modal from "../../components/Modal";
import { errorMessage } from "../../services";
import { getOwners } from "../../services/owners";
import { createSite } from "../../services/sites";
import { useDebounced } from "../../utils/hooks";
import { Spinner } from "../../components/Loader";

// Create a website for an existing customer (store owner). A Sub Admin's website uses up one licence.
export default function AddSiteModal({ user, onClose, onCreated, onNeedCustomer }) {
  const isSuper = user.role === "superadmin";
  const [form, setForm] = useState({ name: "", ownerId: "", plan: "trial" });
  const [ownerSearch, setOwnerSearch] = useState("");
  const [owners, setOwners] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const search = useDebounced(ownerSearch);

  useEffect(() => {
    let live = true;
    getOwners({ search, limit: 20, status: "active" })
      .then(({ data }) => live && setOwners(data.owners))
      .catch((err) => live && setError(errorMessage(err)));
    return () => { live = false; };
  }, [search]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.ownerId) return setError("Choose the customer this website is for.");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), ownerId: form.ownerId };
      if (isSuper) payload.plan = form.plan;
      const { data } = await createSite(payload);
      onCreated(data.message);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal eyebrow="NEW WEBSITE" title="Add website" onClose={onClose}
      description={isSuper ? "Creates a storefront for a customer." : "Uses one website licence from your bulk purchase."}>
      <form onSubmit={submit}>
        {error && <div className="form-error">{error}</div>}
        <label className="admin-field">Store name
          <input required minLength={2} maxLength={60} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Fresh Bakery" />
        </label>
        <label className="admin-field">Find customer
          <input value={ownerSearch} onChange={(e) => setOwnerSearch(e.target.value)} placeholder="Search by name, email or phone" />
        </label>
        <label className="admin-field">Customer
          <select value={form.ownerId} onChange={(e) => setForm({ ...form, ownerId: e.target.value })}>
            <option value="">{owners.length ? "Select a customer…" : "No customers found"}</option>
            {owners.map((o) => <option key={o.id} value={o.id}>{o.name} · {o.email || o.phone}</option>)}
          </select>
        </label>
        {!owners.length && !ownerSearch && (
          <p className="muted">A website needs a customer first. <button type="button" className="text-btn" onClick={onNeedCustomer}>Add a customer →</button></p>
        )}
        {isSuper && (
          <label className="admin-field">Starting plan
            <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
              <option value="trial">Free trial</option>
              <option value="monthly">Monthly (payment received)</option>
              <option value="yearly">Yearly (payment received)</option>
            </select>
          </label>
        )}
        <div className="product-modal-actions">
          <button type="button" className="outline-btn" onClick={onClose}>Cancel</button>
          <button className="primary-btn" disabled={busy}>{busy ? <><Spinner /> Creating</> : "Create website"}</button>
        </div>
      </form>
    </Modal>
  );
}
